import cn from 'classnames'
import { FocusTrap } from 'focus-trap-react'
import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'

import { COURSE_TOOLTIP } from 'components/Editor/EditorElements/courseKit/portalsCurseKit'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { DeviceMode } from 'services/Store/Project/enums'
import type { IBlockMode } from 'services/Store/Project/types'
import { testProps } from 'utils/test/qaData'

import * as s from './CourseModal.module.scss'
import { CloseButtonStyled } from './styled/CloseButtonStyled'

interface ICourseModalProps<Props extends Record<string, unknown>> {
  visible: boolean
  onClose: () => void
  component: React.FC<Props & { onClose: () => void; containerElement: HTMLDivElement | null }>
  props?: Props
  mode: IBlockMode
  maxWidth?: number
  ariaLabelledby?: string
  ariaDescribedby?: string
  ariaLabel?: string
}

const CourseModal = <T extends Record<string, unknown>>({
  component,
  visible,
  onClose,
  props,
  mode,
  maxWidth,
  ariaLabelledby,
  ariaDescribedby,
  ariaLabel,
}: ICourseModalProps<T>) => {
  const [ref, setRef] = React.useState<HTMLDivElement | null>(null)

  const Component = component as ICourseModalProps<T>['component']
  const getContainer = () => document.getElementById(COURSE_TOOLTIP) || document.body
  const desktopMode = mode.deviceMode === DeviceMode.desktop
  const onCloseClick = () => {
    onClose?.()
  }

  const rootCn = cn(s.root, {
    [s.desktop]: desktopMode,
    [s.mobile]: mode.deviceMode === DeviceMode.mobile,
    [s.tablet]: mode.deviceMode === DeviceMode.tablet,
  })
  useEffect(() => {
    const closeOnEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseClick()
      }
    }
    document.addEventListener('keydown', closeOnEsc)
    return () => {
      document.removeEventListener('keydown', closeOnEsc)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  if (!visible) {
    return null
  }

  const content = (
    <FocusTrap>
      <div className={rootCn}>
        <div
          aria-describedby={ariaDescribedby}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          aria-modal='true'
          className={s.contentWrapper}
          ref={setRef}
          role='dialog'
          tabIndex={-1}
        >
          <div className={s.content} style={{ maxWidth }}>
            <Component {...(props as T)} containerElement={ref} onClose={onCloseClick} />
            {desktopMode && (
              <CloseButtonStyled
                $display='desktop'
                aria-label='Close modal'
                onClick={onCloseClick}
                {...testProps({ el: 'modalClose', name: 'courseModal' })}
              >
                <Icon name='otherClose' size={KitSize.M} aria-hidden />
                <div>esc</div>
              </CloseButtonStyled>
            )}
          </div>
          {!desktopMode && <div className={s.closeFix} onClick={onCloseClick} />}
        </div>
        <div className={s.overlay} onClick={onCloseClick}>
          {!desktopMode && (
            <CloseButtonStyled $display='mobile' aria-label='Close modal' onClick={onCloseClick}>
              <Icon name='otherClose' size={KitSize.M} aria-hidden />
            </CloseButtonStyled>
          )}
        </div>
      </div>
    </FocusTrap>
  )

  return createPortal(content, getContainer())
}

export default CourseModal
